import { TXProvider } from '@transifex/react';
import type { ReactNode } from 'react';

import { setAnalytics, type OnboardingVendors } from '@/modules/analytics';
import {
  setApiClient,
  setAuthFailureHandler,
  type AuthFailureHandler,
  type OnboardingApiClient,
} from '@/modules/api';
import { setRuntimeConfig, type OnboardingConfig } from '@/modules/runtimeConfig';
import { configureOnboardingTx, onboardingTx, useLanguageSetting } from '@/modules/transifex';

import { ScreenViewTracker } from '../components/ScreenViewTracker';

type OnboardingRootProps = OnboardingVendors & {
  /**
   * The leaf values the flow reads from the environment — `vidaEnv`, `lobPublishableKey`,
   * `streamChatApiKey`, `transifexToken`. See `OnboardingConfig`.
   *
   * Deliberately small. Vendor credentials are **not** here: PostHog and Datadog belong to
   * whoever mounts them, so their keys travel to `createVidaVendors` instead, and a host
   * that injects `analytics` never states them at all.
   *
   * Required from a consumer, optional for the standalone app. `babel-preset-expo` only
   * inlines `EXPO_PUBLIC_*` for the app being built, so once this package sits in a
   * consumer's `node_modules` every direct read is `undefined` at any scope. The consumer
   * reads the variables where inlining works — its own `src/` — and hands the values across.
   */
  config?: OnboardingConfig;
  /**
   * Language to render in, as a system locale (`en-US`, `es-MX`) — normalized to a
   * Transifex locale internally. Defaults to the device locale, which is the standalone
   * behaviour.
   *
   * A host passes this so onboarding follows the language the rest of its UI is already
   * showing, which is not always the device's: VidaReactNative lets a member choose one
   * in-app. What a host does *not* do is pass its Transifex instance — our strings live in
   * a different project (`EXPO_PUBLIC_TRANSIFEX_TOKEN`, not
   * `EXPO_PUBLIC_LOGIN_TRANSIFEX_TOKEN`), so its `tx` could only render our source English.
   * Transifex is the one vendor we own in every mount; only the *choice of language* is the
   * host's. See `docs/vendor-ownership-proposal.md` §1.
   */
  locale?: string;
  /**
   * HTTP client for every API call. Defaults to our own axios client (`apiBaseUrl` from
   * `config`, tokens in SecureStore).
   *
   * A host passes its own because **the session is the host's**: embedded, the member is
   * already signed in, and the host holds the token, owns refresh, and decides what a 401
   * means. Two clients means two ideas of who is signed in, racing to refresh one token. See
   * `OnboardingApiClient` for the two behaviours the structural type cannot enforce (it must
   * reject on non-2xx, and it should camelize).
   *
   * **`config.apiBaseUrl` is required whether or not you pass this.** Token refresh always runs
   * on our own `authClient` — the token is deliberately not injected, because the three repos
   * share the same storage keys — so `/v1/auth/refresh` goes to our base URL even when every
   * other call goes to yours. Omit it and the first refresh fails with a config error.
   */
  apiClient?: OnboardingApiClient;
  /**
   * Called when the flow finds the stored session unusable — a refresh the server rejected, a
   * stale access token with nothing to refresh it, or a 401 the refresh path could not rescue.
   * The `reason` distinguishes them; see `AuthFailureReason`.
   *
   * **Omitted means nothing is cleared,** and that is the correct default rather than an
   * oversight. The token keys are shared across onboarding, `@vidahealth/login` and
   * VidaReactNative, so deleting them signs the member out of the host app too. A host that
   * owns refresh already has its own answer to a dead session and does not want ours running
   * underneath it.
   *
   * We report; the session's owner decides. The standalone app is the owner in its own
   * process, so `app/_layout.tsx` passes `clearAuthTokens` — which is what makes our own app
   * exercise the path a consumer takes instead of relying on a built-in default.
   *
   * The flow recovers either way: a reported failure still leaves requests unauthenticated and
   * still resets `RulesEngineProvider` through `subscribeToUnauthorized`. Only the storage
   * write is yours to authorize.
   */
  onAuthFailure?: AuthFailureHandler;
  /**
   * Rendered in place of `children` while a non-English locale's translations are still
   * loading. Defaults to nothing.
   *
   * English users never reach it, so this is the 5% path — but "nothing" is the wrong default
   * to be stuck with, because the caller has usually just dismissed its own splash or spinner
   * on the assumption that we render something. That is exactly what the standalone app does:
   * `SplashScreen.hideAsync()` fires once fonts load, and without a fallback a Spanish device
   * went from splash straight to an unthemed blank screen for the length of the CDS fetch.
   *
   * A prop rather than a built-in loading screen: what belongs on screen during the wait is
   * the caller's chrome, not ours, and only the caller knows whether it has already painted
   * something behind us.
   */
  fallback?: ReactNode;
  children: ReactNode;
};

/**
 * The onboarding package's root: configuration and ports in, a mounted flow out.
 *
 * ## What it does, in order, during render
 *
 * ```
 * setRuntimeConfig(config)             // leaf env values, before anything can read them
 * setAnalytics(analytics)              // where telemetry goes — the host's, ours, or nowhere
 * setApiClient(apiClient)              // whose session the flow runs on
 * setAuthFailureHandler(onAuthFailure) // who decides what a dead session means
 * configureOnboardingTx()              // our own Transifex instance, pointed at our token
 * ```
 *
 * **During render, not in an effect.** An effect fires after children commit — late enough
 * for a boot-time request to have gone out against an unpublished config, for a first
 * `$pageview` to have been dropped, and for a child's `useT()` to have subscribed to an
 * uninitialized Transifex instance. Every publish is idempotent, so re-renders and
 * StrictMode's double invoke are harmless.
 *
 * ## Ownership is expressed by what you pass
 *
 * There is no `embedded` flag. There was one, and it was a single boolean standing in for
 * four independent decisions while only ever gating Datadog; it existed because
 * credentials cannot express ownership of a process-wide singleton. Under ports the
 * question does not arise:
 *
 * | Vendor    | Standalone                        | Embedded                                |
 * | --------- | --------------------------------- | --------------------------------------- |
 * | Transifex | we create `onboardingTx`          | **same** — different project per package |
 * | PostHog   | `createVidaVendors` builds it     | host's, via `analytics`                  |
 * | Datadog   | `createVidaVendors` inits it      | host's, via `analytics`                  |
 * | API       | our axios + SecureStore           | host's, via `apiClient`                  |
 * | Session   | we clear it, via `onAuthFailure`  | host's — pass nothing and we touch none  |
 *
 * Hand us an `analytics` and we use it; hand us nothing and telemetry no-ops. The full
 * reasoning — why sharing the host's PostHog is *more* correct and not just cheaper — is in
 * `AnalyticsPort` and `docs/vendor-ownership-proposal.md` §1.
 *
 * The `Session` row is the one where the default had to be inverted rather than inherited.
 * Every other row degrades to "nothing happens" when a caller passes nothing, which is
 * harmless; a session that clears itself by default is not, because the token keys are shared
 * and clearing them signs the member out of the host. So the flow reports and the owner acts —
 * see `onAuthFailure` and `api/auth/authFailure.ts`.
 *
 * ## What is still app-owned
 *
 * `AppThemeProvider`, `RulesEngineProvider` and `OnboardingStepsProvider` stay in
 * `app/_layout.tsx`. The only surface a consumer mounts today is the dynamic-assessment
 * screen, so nothing yet needs the rules engine or our theme from a shared root; that
 * composition moves here when the onboarding steps are exposed, which is also when a React
 * context earns its place (the host's callbacks and mount path, as in login's `contexts/`).
 */
export function OnboardingRoot({
  config,
  analytics,
  Provider: VendorProvider,
  trackScreens = false,
  locale,
  apiClient,
  onAuthFailure,
  fallback = null,
  children,
}: OnboardingRootProps) {
  if (config) setRuntimeConfig(config);
  setAnalytics(analytics);
  setApiClient(apiClient);
  setAuthFailureHandler(onAuthFailure);
  configureOnboardingTx();

  // Sync `onboardingTx` to the requested (or device) locale, and report when it's safe to
  // render. Ordered after `configureOnboardingTx()`: `setCurrentLocale` on an uninitialized
  // instance would fetch under an empty token.
  const { ready: localeReady } = useLanguageSetting(onboardingTx, locale);

  // Hold `children` back until translations are in place — a readiness gate `LoginRoot` does
  // not have. It syncs the locale in an effect and accepts a flash of English source
  // strings; ours blocks, because `@transifex/react` sets up `useT()` subscriptions in
  // passive effects and a `TRANSLATIONS_FETCHED` that lands first leaves components stuck in
  // English even with a warm cache.
  //
  // English users (95%) are ready on the first render and never reach this branch. The other
  // 5% get `fallback`, which defaults to nothing — but a caller that has already dismissed
  // its own splash should pass something, or the wait reads as a blank screen. See the prop.
  //
  // Gates the CONTENT, not the providers: `TXProvider`, the vendor `Provider` and
  // `ScreenViewTracker` all stay mounted across the swap.
  //
  // `children` itself does mount fresh when the gate opens, since it replaces `fallback` in the
  // same slot — which is why `useLanguageSetting` never sets `ready` back to false. A later
  // `locale` change must not swap back to `fallback`, or it would unmount `RulesEngineProvider`
  // out from under a member's in-progress draft.
  const content = localeReady ? children : fallback;

  // Vendor providers outermost so their init effects run before any child reports; `TXProvider`
  // innermost so every `useT()` / `<T>` below is scoped to our instance and a host's `tx.init`
  // cannot silently drop our strings to English source. The dynamic-assessment route nests its
  // own `TXProvider` for `assessmentTx` inside this one, which correctly wins for that subtree.
  //
  // `ScreenViewTracker` is a SIBLING of the content, not a layer around it: it renders `null`
  // and exists only for its effect, so wrapping would add a tree level for nothing. It is
  // deliberately outside the `localeReady` gate: a route change should be reported whether or
  // not translations have landed, and gating it would silently drop the first screen view of
  // every non-English session.
  //
  // Mounting used to be the whole on/off switch. It cannot be any more, because a route change
  // now produces two signals with different owners: the VIEW (`trackScreen`, only ours to send
  // when `trackScreens` says we own the view tree) and the STEP (`trackStep`, safe anywhere and
  // the member a host implements). So mount when EITHER has a consumer, and pass the view
  // decision down.
  //
  // Still conditional rather than always-on, and that matters beyond tidiness: this component
  // calls `usePathname`, so mounting it unconditionally would make expo-router a hard runtime
  // requirement of `OnboardingRoot` for every host, including one that wants no route telemetry
  // at all. Both conditions are explicit caller choices.
  const tracksSteps = Boolean(analytics?.trackStep);
  const tree = (
    <>
      {trackScreens || tracksSteps ? <ScreenViewTracker ownsViews={trackScreens} /> : null}
      <TXProvider instance={onboardingTx}>{content}</TXProvider>
    </>
  );

  return VendorProvider ? <VendorProvider>{tree}</VendorProvider> : tree;
}
